// src/components/admin/SettingsTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Palette,
  ShieldCheck,
  CreditCard,
  Bell,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  Sparkles,
  Megaphone,
  Clock,
  Zap,
  Phone,
  Smartphone,
  WalletCards,
  Globe,
  RefreshCw,
  Type,
  Check,
  ShoppingBag,
} from "lucide-react";

interface SettingsTabProps {
  subdomain: string;
  theme?: "dark" | "light";
}

// 🌟 ১০+ আকর্ষণীয় প্রিমিয়াম ই-কমার্স থিম কালার প্যালেট
const PRESET_THEMES = [
  {
    id: "fresh_emerald",
    name: "Fresh Emerald",
    category: "Organic & Grocery",
    primary: "#059669",
    secondary: "#047857",
  },
  {
    id: "modern_royal",
    name: "Modern Royal",
    category: "Tech & Gadgets",
    primary: "#2563EB",
    secondary: "#1D4ED8",
  },
  {
    id: "luxury_rose",
    name: "Luxury Rose",
    category: "Fashion & Cosmetics",
    primary: "#E11D48",
    secondary: "#BE123C",
  },
  {
    id: "golden_sunset",
    name: "Golden Sunset",
    category: "Jewelry & Luxury",
    primary: "#D97706",
    secondary: "#B45309",
  },
  {
    id: "neon_cyber",
    name: "Cyber Violet",
    category: "Gaming & Accessories",
    primary: "#7C3AED",
    secondary: "#5B21B6",
  },
  {
    id: "ocean_breeze",
    name: "Ocean Breeze",
    category: "Lifestyle & Sports",
    primary: "#0284C7",
    secondary: "#0369A1",
  },
  {
    id: "coral_blush",
    name: "Coral Blush",
    category: "Beauty & Skincare",
    primary: "#F43F5E",
    secondary: "#E11D48",
  },
  {
    id: "mint_serenity",
    name: "Mint Serenity",
    category: "Health & Wellness",
    primary: "#0D9488",
    secondary: "#115E59",
  },
  {
    id: "tangerine_pop",
    name: "Tangerine Pop",
    category: "Kids & Streetwear",
    primary: "#EA580C",
    secondary: "#C2410C",
  },
  {
    id: "royal_amethyst",
    name: "Royal Amethyst",
    category: "Perfumes & Gifts",
    primary: "#9333EA",
    secondary: "#7E22CE",
  },
  {
    id: "monochrome_onyx",
    name: "Monochrome Onyx",
    category: "Minimalist & Apparel",
    primary: "#18181B",
    secondary: "#27272A",
  },
  {
    id: "crimson_flame",
    name: "Crimson Flame",
    category: "Automotive & Tools",
    primary: "#DC2626",
    secondary: "#B91C1C",
  },
];

const FONT_OPTIONS = [
  { id: "Poppins", name: "Poppins (Modern & Clean)" },
  { id: "Inter", name: "Inter (Minimalist & Professional)" },
  { id: "Plus Jakarta Sans", name: "Plus Jakarta Sans (Trendy & Sleek)" },
  { id: "Outfit", name: "Outfit (Bold & Elegant)" },
  { id: "Playfair Display", name: "Playfair Display (Luxury Serif)" },
  { id: "Roboto", name: "Roboto (Classic & Balanced)" },
];

export default function SettingsTab({ subdomain, theme: propTheme }: SettingsTabProps) {
  // 🌟 অটো-থিম ডিটেকশন (Prop + DOM Class + LocalStorage)
  const [internalTheme, setInternalTheme] = useState<"dark" | "light">(propTheme || "dark");

  useEffect(() => {
    if (propTheme) {
      setInternalTheme(propTheme);
      return;
    }

    const checkTheme = () => {
      if (typeof window === "undefined") return;
      const isDocLight =
        document.documentElement.classList.contains("light") ||
        localStorage.getItem("admin_theme") === "light";
      setInternalTheme(isDocLight ? "light" : "dark");
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", checkTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", checkTheme);
    };
  }, [propTheme]);

  const isLight = (propTheme || internalTheme) === "light";

  const [activeSection, setActiveSection] = useState<
    "theme" | "security" | "gateways" | "subscription" | "notifications"
  >("theme");

  const [loading, setLoading] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const getApiUrl = useCallback(
    (endpoint: string) => {
      if (typeof window !== "undefined") {
        const host = window.location.hostname;
        if (host === "localhost" || host === "127.0.0.1" || host.includes("localhost")) {
          return `http://${subdomain}.localhost:8000${endpoint}`;
        }
        return endpoint;
      }
      return endpoint;
    },
    [subdomain]
  );

  const getAuthToken = () => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";
  };

  // ১. থিম ও ব্র্যান্ডিং স্টেট
  const [themeSettings, setThemeSettings] = useState({
    theme: "fresh_emerald",
    primary_color: "#059669",
    secondary_color: "#047857",
    font_family: "Poppins",
    show_announcement_bar: true,
    announcement_text: "Sign up and GET 20% OFF on your first order!",
    announcement_phone: "+880 1700-000000",
  });

  // ২. ২FA সিকিউরিটি স্টেট
  const [phoneNumber, setPhoneNumber] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // ৩. পাসওয়ার্ড স্টেট
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ৪. পেমেন্ট গেটওয়ে স্টেট
  const [selectedGatewayType, setSelectedGatewayType] = useState<string>("bkash");
  const [gatewayTitle, setGatewayTitle] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [senderId, setSenderId] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [isGatewayActive, setIsGatewayActive] = useState(false);
  const [isSandbox, setIsSandbox] = useState(true);
  const [savedGateways, setSavedGateways] = useState<any[]>([]);

  // ৫. সাবস্ক্রিপশন স্টেট
  const [subscription, setSubscription] = useState<any>({
    plan_name: "Loading...",
    monthly_price: 1500,
    price: "৳...",
    status: "Active",
    days_left: 0,
    expiry_date: "Loading...",
    can_renew: false,
    days_until_renewal_opens: 0,
    billing_cycle: "monthly",
  });

  const [adminNotifications, setAdminNotifications] = useState<any[]>([]);

  // পেমেন্ট গেটওয়ে তালিকা ফেচ করা
  const fetchGateways = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl("/api/v1/payments/merchant/gateways/"));
      if (res.ok) {
        const data = await res.json();
        setSavedGateways(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error("Failed to load gateways:", e);
    }
  }, [getApiUrl]);

  // নির্বাচিত গেটওয়ের ডাটা ইনপুট বক্সে সেট করা
  useEffect(() => {
    const current = savedGateways.find((g) => g.gateway_type === selectedGatewayType);
    if (current) {
      setGatewayTitle(current.title || "");
      setApiKey(current.api_key || "");
      setApiSecret(current.api_secret || "");
      setSenderId(current.sender_id || "");
      setApiUrl(current.api_url || "");
      setIsGatewayActive(Boolean(current.is_active));
      setIsSandbox(current.is_sandbox !== false);
    } else {
      setGatewayTitle(
        selectedGatewayType === "bkash"
          ? "bKash Online Payment"
          : selectedGatewayType === "nagad"
          ? "Nagad Payment Gateway"
          : selectedGatewayType === "sslcommerz"
          ? "SSLCommerz Checkout"
          : "Online Payment"
      );
      setApiKey("");
      setApiSecret("");
      setSenderId("");
      setApiUrl("");
      setIsGatewayActive(false);
      setIsSandbox(true);
    }
  }, [selectedGatewayType, savedGateways]);

  // সাবস্ক্রিপশন ডাটা ফেচ করা
  const fetchSubscriptionData = useCallback(async () => {
    setSubLoading(true);
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { Accept: "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      let subData: any = null;
      try {
        const subRes = await fetch(
          getApiUrl(`/api/v1/subscriptions/subscriptions/current/?subdomain=${encodeURIComponent(subdomain)}`),
          { headers }
        );
        if (subRes.ok) subData = await subRes.json();
      } catch {}

      if (!subData) {
        try {
          const directRes = await fetch(
            getApiUrl(`/api/v1/subscriptions/current/?subdomain=${encodeURIComponent(subdomain)}`),
            { headers }
          );
          if (directRes.ok) subData = await directRes.json();
        } catch {}
      }

      if (subData) {
        const daysLeft = subData.days_left !== undefined ? Number(subData.days_left) : 0;
        const canRenew = subData.can_renew !== undefined ? Boolean(subData.can_renew) : daysLeft <= 10;

        setSubscription({
          id: subData.id,
          plan_name: subData.plan_name || "Growth Spark",
          monthly_price: Number(subData.monthly_price || 1500),
          price: subData.price || `৳${Number(subData.monthly_price || 1500).toLocaleString()} / মাস`,
          status: subData.status || "Active",
          days_left: daysLeft,
          expiry_date: subData.expiry_date || "18 October, 2026",
          can_renew: canRenew,
          days_until_renewal_opens: Number(subData.days_until_renewal_opens || Math.max(0, daysLeft - 10)),
          billing_cycle: subData.billing_cycle || "monthly",
        });
      }
    } catch (err) {
      console.error("Subscription fetch error:", err);
    } finally {
      setSubLoading(false);
    }
  }, [getApiUrl, subdomain]);

  // সমস্ত সেটিংস লোড করা
  const fetchSettings = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { Accept: "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      let merchantEmail = "";
      try {
        const stored = localStorage.getItem("merchant_user") || localStorage.getItem("user");
        if (stored) {
          const parsed = JSON.parse(stored);
          merchantEmail = parsed.email || "";
        }
      } catch {}

      const [settingsRes, notifRes, securityRes] = await Promise.all([
        fetch(getApiUrl("/api/v1/stores/settings/"), { headers }),
        fetch(getApiUrl("/api/v1/stores/merchant/admin-notifications/"), { headers }),
        fetch(getApiUrl(`/api/v1/stores/merchant/security/?email=${encodeURIComponent(merchantEmail)}`), { headers }).catch(() => null),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setThemeSettings((prev) => ({
          ...prev,
          theme: data.theme || "fresh_emerald",
          primary_color: data.primary_color || "#059669",
          secondary_color: data.secondary_color || "#047857",
          font_family: data.font_family || "Poppins",
          show_announcement_bar: data.show_announcement_bar !== false,
          announcement_text: data.announcement_text || prev.announcement_text,
          announcement_phone: data.announcement_phone || prev.announcement_phone,
        }));
      }

      if (securityRes && securityRes.ok) {
        const secData = await securityRes.json();
        if (secData.phone) setPhoneNumber(secData.phone);
        if (secData.two_factor_enabled !== undefined) {
          setTwoFactorEnabled(Boolean(secData.two_factor_enabled));
        }
      }

      if (notifRes.ok) setAdminNotifications(await notifRes.json());

      await fetchSubscriptionData();
      await fetchGateways();
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  }, [getApiUrl, fetchGateways, fetchSubscriptionData]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSelectPreset = (preset: (typeof PRESET_THEMES)[0]) => {
    setThemeSettings((prev) => ({
      ...prev,
      theme: preset.id,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
    }));
  };

  const handleSaveTheme = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSaveSuccess(false);
    setErrorMessage("");

    try {
      const res = await fetch(getApiUrl("/api/v1/stores/settings/"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(themeSettings),
      });

      if (res.ok) {
        setSaveSuccess(true);
        setSuccessMessage("দোকানের থিম ও ব্র্যান্ডিং কালার সফলভাবে সংরক্ষিত হয়েছে!");
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setErrorMessage("থিম সেটিংস আপডেট করা যায়নি।");
      }
    } catch {
      setErrorMessage("সার্ভারের সাথে সংযোগ বিচ্ছিন্ন হয়েছে।");
    } finally {
      setLoading(false);
    }
  };

  const handleRenewSubscription = async () => {
    setRenewalLoading(true);
    setErrorMessage("");
    try {
      const res = await fetch(getApiUrl("/api/v1/payments/transactions/initiate-subscription/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_name: subscription.plan_name,
          amount: Number(subscription.monthly_price || 1500),
          billing_cycle: subscription.billing_cycle || "monthly",
          subdomain,
        }),
      });
      const data = await res.json();
      if (res.ok && data.gateway_url) {
        window.location.href = data.gateway_url;
      } else {
        setErrorMessage(data.error || "পেমেন্ট গেটওয়েতে সংযোগ করা সম্ভব হয়নি।");
      }
    } catch {
      setErrorMessage("সার্ভারে সমস্যা হয়েছে। পুনরায় চেষ্টা করুন।");
    } finally {
      setRenewalLoading(false);
    }
  };

  const handleSaveGateway = async (e: React.FormEvent) => {
    e.preventDefault();
    setGatewayLoading(true);
    setErrorMessage("");
    setSaveSuccess(false);
    try {
      const res = await fetch(getApiUrl("/api/v1/payments/merchant/gateways/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gateway_type: selectedGatewayType,
          title: gatewayTitle.trim(),
          api_key: apiKey.trim(),
          api_secret: apiSecret.trim(),
          sender_id: senderId.trim(),
          api_url: apiUrl.trim(),
          is_sandbox: isSandbox,
          is_active: isGatewayActive,
          subdomain,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSaveSuccess(true);
        setSuccessMessage(`${gatewayTitle || selectedGatewayType} সেটিংস সংরক্ষিত হয়েছে!`);
        fetchGateways();
        setTimeout(() => setSaveSuccess(false), 4500);
      } else {
        setErrorMessage(data.error || "পেমেন্ট গেটওয়ে সংরক্ষণ করা সম্ভব হয়নি।");
      }
    } catch {
      setErrorMessage("সার্ভারে সমস্যা হয়েছে।");
    } finally {
      setGatewayLoading(false);
    }
  };

  const handleSave2FASettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSaveSuccess(false);
    if (twoFactorEnabled && !phoneNumber.trim()) {
      setErrorMessage("2-Step Verification অন করতে ফোন নম্বর প্রদান করা আবশ্যক!");
      return;
    }
    setSecurityLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/api/v1/stores/merchant/security/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          phone: phoneNumber.trim(),
          enable_2fa: twoFactorEnabled,
        }),
      });
      if (res.ok) {
        setSaveSuccess(true);
        setSuccessMessage("২FA সিকিউরিটি সফলভাবে আপডেট হয়েছে!");
        setTimeout(() => setSaveSuccess(false), 4000);
      }
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.trim().length < 6) {
      setErrorMessage("নতুন পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("নতুন পাসওয়ার্ড এবং কনফার্ম পাসওয়ার্ড মিলছে না!");
      return;
    }
    setPasswordLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/api/v1/stores/merchant/change-password/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword.trim(),
        }),
      });
      if (res.ok) {
        setSaveSuccess(true);
        setSuccessMessage("পাসওয়ার্ড সফলভাবে পরিবর্তন হয়েছে!");
        localStorage.clear();
        setTimeout(() => window.location.reload(), 1200);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    /* 🌟 OverviewTab-এর মতো ফুল-উইডথ রেসপনসিভ কমপ্যাক্ট প্যাডিং */
    <div className="w-full px-3.5 sm:px-5 lg:px-6 py-4 sm:py-6 space-y-5 font-sans transition-colors duration-200">
      <div>
        <h2 className={`text-lg sm:text-xl font-black flex items-center gap-2.5 ${isLight ? "text-slate-900" : "text-white"}`}>
          <Sparkles className="w-5 h-5 text-emerald-500" />
          <span>Merchant Store Settings</span>
        </h2>
        <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
          Manage payment gateways, store appearance, 2FA security, and subscription billing
        </p>
      </div>

      <div
        className={`flex items-center gap-1.5 p-1.5 border rounded-2xl overflow-x-auto scrollbar-none text-xs font-bold transition-all ${
          isLight ? "bg-white border-slate-200 shadow-sm" : "bg-slate-900 border-slate-800"
        }`}
      >
        {[
          { id: "theme", label: "Theme & Branding", icon: Palette },
          { id: "subscription", label: "Subscription & Billing", icon: CreditCard },
          { id: "gateways", label: "Payment Gateways", icon: WalletCards },
          { id: "security", label: "Security & 2FA", icon: ShieldCheck },
          { id: "notifications", label: "Announcements & Alerts", icon: Bell },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeSection === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSection(tab.id as any);
                setErrorMessage("");
                setSaveSuccess(false);
              }}
              className={`flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/30"
                  : isLight
                  ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/60"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {saveSuccess && (
        <div
          className={`p-3.5 rounded-2xl border text-xs flex items-center gap-2 animate-in fade-in ${
            isLight
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
          }`}
        >
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div
          className={`p-3.5 rounded-2xl border text-xs flex items-center gap-2 animate-in fade-in ${
            isLight
              ? "bg-rose-50 border-rose-200 text-rose-700"
              : "bg-rose-500/10 border-rose-500/20 text-rose-400"
          }`}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* 🌟 1. THEME & BRANDING সেকশন */}
      {activeSection === "theme" && (
        <form onSubmit={handleSaveTheme} className="space-y-6 text-xs font-sans">
          {/* প্রিসেট থিম পিকার গ্রিড */}
          <div
            className={`border rounded-3xl p-5 sm:p-6 space-y-4 shadow-md transition-all ${
              isLight
                ? "bg-white border-slate-200/90 shadow-slate-200/50"
                : "bg-slate-900 border-slate-800 shadow-xl"
            }`}
          >
            <div className={`flex items-center justify-between border-b pb-3 ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <div>
                <h3 className={`text-sm font-black flex items-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
                  <Palette className="w-4 h-4 text-emerald-500" />
                  <span>Preset Color Themes</span>
                </h3>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                  আপনার ক্যাটাগরি অনুযায়ী যেকোনো আকর্ষণীয় প্যালেট এক ক্লিকে পছন্দ করুন।
                </p>
              </div>
              <span
                className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                  isLight
                    ? "bg-slate-100 text-slate-700 border-slate-200"
                    : "bg-slate-800 text-slate-300 border-slate-700"
                }`}
              >
                12 Premium Themes
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-2">
              {PRESET_THEMES.map((theme) => {
                const isSelected =
                  themeSettings.primary_color.toLowerCase() === theme.primary.toLowerCase() &&
                  themeSettings.secondary_color.toLowerCase() === theme.secondary.toLowerCase();

                return (
                  <button
                    type="button"
                    key={theme.id}
                    onClick={() => handleSelectPreset(theme)}
                    className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative flex flex-col justify-between h-24 ${
                      isSelected
                        ? isLight
                          ? "bg-slate-50 border-emerald-500 ring-2 ring-emerald-500/30 shadow-md"
                          : "bg-slate-800 border-emerald-500 ring-2 ring-emerald-500/30 shadow-lg"
                        : isLight
                        ? "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                        : "bg-slate-950/70 border-slate-800 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center -space-x-1.5">
                        <span
                          className={`w-5 h-5 rounded-full border shadow-sm ${isLight ? "border-white" : "border-slate-900"}`}
                          style={{ backgroundColor: theme.primary }}
                        />
                        <span
                          className={`w-5 h-5 rounded-full border shadow-sm ${isLight ? "border-white" : "border-slate-900"}`}
                          style={{ backgroundColor: theme.secondary }}
                        />
                      </div>

                      {isSelected && (
                        <span className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </span>
                      )}
                    </div>

                    <div>
                      <h4 className={`text-xs font-bold truncate ${isLight ? "text-slate-900" : "text-white"}`}>{theme.name}</h4>
                      <span className="text-[10px] text-slate-400 block truncate">{theme.category}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* কাস্টম কালার ও ফন্ট সিলেক্টর + লাইভ প্রিভিউ */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
            {/* কাস্টম কালার ইনপুট */}
            <div
              className={`lg:col-span-7 border rounded-3xl p-5 sm:p-6 space-y-4 shadow-md transition-all ${
                isLight ? "bg-white border-slate-200/90 shadow-slate-200/50" : "bg-slate-900 border-slate-800 shadow-xl"
              }`}
            >
              <h3 className={`text-sm font-black flex items-center gap-2 border-b pb-3 ${
                isLight ? "text-slate-900 border-slate-100" : "text-white border-slate-800"
              }`}>
                <Sparkles className="w-4 h-4 text-emerald-500" />
                <span>Custom Brand Colors & Typography</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>Primary Store Color</label>
                  <div className={`flex items-center gap-2.5 p-2 rounded-xl border ${
                    isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                  }`}>
                    <input
                      type="color"
                      value={themeSettings.primary_color}
                      onChange={(e) =>
                        setThemeSettings({ ...themeSettings, primary_color: e.target.value })
                      }
                      className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border-0 shrink-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.primary_color}
                      onChange={(e) =>
                        setThemeSettings({ ...themeSettings, primary_color: e.target.value })
                      }
                      className={`flex-1 bg-transparent border-0 font-mono uppercase font-bold text-xs outline-none ${
                        isLight ? "text-slate-900" : "text-white"
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>Secondary Accent Color</label>
                  <div className={`flex items-center gap-2.5 p-2 rounded-xl border ${
                    isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                  }`}>
                    <input
                      type="color"
                      value={themeSettings.secondary_color}
                      onChange={(e) =>
                        setThemeSettings({ ...themeSettings, secondary_color: e.target.value })
                      }
                      className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border-0 shrink-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.secondary_color}
                      onChange={(e) =>
                        setThemeSettings({ ...themeSettings, secondary_color: e.target.value })
                      }
                      className={`flex-1 bg-transparent border-0 font-mono uppercase font-bold text-xs outline-none ${
                        isLight ? "text-slate-900" : "text-white"
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 pt-2">
                <label className={`font-bold flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                  <Type className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Storefront Font Family</span>
                </label>
                <select
                  value={themeSettings.font_family}
                  onChange={(e) =>
                    setThemeSettings({ ...themeSettings, font_family: e.target.value })
                  }
                  className={`w-full px-4 py-2.5 rounded-xl border text-xs outline-none focus:border-emerald-500 cursor-pointer ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-800" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* লাইভ স্টোরফ্রন্ট প্রিভিউ কার্ড */}
            <div
              className={`lg:col-span-5 border rounded-3xl p-5 sm:p-6 space-y-4 flex flex-col justify-between shadow-md transition-all ${
                isLight ? "bg-white border-slate-200/90 shadow-slate-200/50" : "bg-slate-900 border-slate-800 shadow-xl"
              }`}
            >
              <div>
                <span className={`text-[10px] font-black uppercase tracking-wider block mb-2 ${
                  isLight ? "text-slate-500" : "text-slate-400"
                }`}>
                  Live Storefront Preview
                </span>

                <div className={`p-4 rounded-2xl border space-y-3 ${
                  isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
                }`}>
                  <div className={`flex items-center justify-between border-b pb-2 ${isLight ? "border-slate-200" : "border-slate-800/80"}`}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-[10px] font-black shadow-xs"
                        style={{ backgroundColor: themeSettings.primary_color }}
                      >
                        {subdomain[0]?.toUpperCase() || "S"}
                      </span>
                      <span className={`font-black text-xs capitalize ${isLight ? "text-slate-900" : "text-white"}`}>{subdomain}</span>
                    </div>

                    <span
                      className="px-2 py-0.5 rounded-md text-[9px] font-bold text-white shadow-xs"
                      style={{ backgroundColor: themeSettings.secondary_color }}
                    >
                      Cart (2)
                    </span>
                  </div>

                  <div
                    className="p-3 rounded-xl text-white space-y-1 shadow-sm"
                    style={{
                      background: `linear-gradient(135deg, ${themeSettings.primary_color}, ${themeSettings.secondary_color})`,
                    }}
                  >
                    <span className="text-[10px] font-black uppercase tracking-wide opacity-90 block">
                      Special Offer 20% OFF
                    </span>
                    <p className="text-xs font-black">Discover New Arrivals</p>
                  </div>

                  <button
                    type="button"
                    className="w-full py-2 rounded-xl text-white font-black text-[11px] transition-transform active:scale-95 shadow-md flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: themeSettings.primary_color }}
                  >
                    <ShoppingBag className="w-3.5 h-3.5" />
                    <span>Shop Now</span>
                  </button>
                </div>
              </div>

              <div className={`pt-2 text-[11px] flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>সেভ করার সাথে সাথে স্টোরফ্রন্টে এই পরিবর্তন সক্রিয় হবে।</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-black transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/30 cursor-pointer disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>Save Appearance</span>
            </button>
          </div>
        </form>
      )}

      {/* 🌟 2. SUBSCRIPTION & BILLING সেকশন */}
      {activeSection === "subscription" && (
        <div className="space-y-6 text-xs font-sans">
          {subLoading ? (
            <div
              className={`p-12 rounded-3xl border flex items-center justify-center gap-3 text-xs ${
                isLight ? "bg-white border-slate-200 text-slate-500 shadow-sm" : "bg-slate-900 border-slate-800 text-slate-400"
              }`}
            >
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <span>ডাটাবেজ থেকে সাবস্ক্রিপশন তথ্য লোড হচ্ছে...</span>
            </div>
          ) : (
            <>
              <div
                className={`border rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 transition-all ${
                  isLight
                    ? "bg-gradient-to-r from-emerald-50 to-slate-50 border-emerald-200 shadow-sm"
                    : "bg-gradient-to-r from-emerald-950/50 via-slate-900 to-slate-900 border-emerald-500/30 shadow-xl"
                }`}
              >
                <div className="space-y-2">
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border inline-block ${
                      isLight
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                    }`}
                  >
                    Current Subscription
                  </span>
                  <h3 className={`text-2xl sm:text-3xl font-black ${isLight ? "text-slate-900" : "text-white"}`}>
                    {subscription.plan_name}
                  </h3>
                  <p className={`font-medium ${isLight ? "text-slate-600" : "text-slate-300"}`}>
                    Expiry Date: <strong className="text-emerald-500">{subscription.expiry_date}</strong> (
                    <span className={subscription.days_left <= 10 ? "text-amber-500 font-bold" : isLight ? "text-slate-600" : "text-slate-300"}>
                      {subscription.days_left} days remaining
                    </span>)
                  </p>
                </div>

                <div className="text-left md:text-right space-y-2">
                  <span className={`text-2xl sm:text-3xl font-black font-mono block ${isLight ? "text-slate-900" : "text-white"}`}>
                    {subscription.price}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border inline-flex items-center gap-1.5 ${
                      subscription.status === "Active"
                        ? isLight
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                        : isLight
                        ? "bg-rose-100 text-rose-800 border-rose-200"
                        : "bg-rose-500/10 text-rose-400 border-rose-500/30"
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>{subscription.status}</span>
                  </span>
                </div>
              </div>

              {subscription.can_renew || subscription.days_left <= 10 ? (
                <div
                  className={`p-6 rounded-3xl border space-y-4 ${
                    isLight ? "bg-amber-50/80 border-amber-200" : "bg-amber-500/10 border-amber-500/30"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <h4 className={`text-sm font-black ${isLight ? "text-slate-900" : "text-white"}`}>
                        আপনার সাবস্ক্রিপশনের মেয়াদ শেষ হতে মাত্র {subscription.days_left} দিন বাকি!
                      </h4>
                      <p className={`text-xs leading-relaxed ${isLight ? "text-slate-600" : "text-slate-400"}`}>
                        স্টোরের ডাটা ও লাইভ সার্ভিস নিরবচ্ছিন্ন রাখতে পরবর্তী বিলিং মেয়াদের ফি এখনই রিনিউ করুন।
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
                    <button
                      type="button"
                      disabled={renewalLoading}
                      onClick={handleRenewSubscription}
                      className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                    >
                      {renewalLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      <span>Renew Subscription Now ({subscription.price})</span>
                    </button>

                    <button
                      type="button"
                      onClick={fetchSubscriptionData}
                      className={`p-3 rounded-full transition-colors cursor-pointer border ${
                        isLight
                          ? "bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-sm"
                          : "bg-slate-800 hover:bg-slate-700 text-slate-300 border-transparent"
                      }`}
                      title="Refresh Status"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`p-6 rounded-3xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs transition-all ${
                    isLight ? "bg-white border-slate-200 text-slate-600 shadow-sm" : "bg-slate-900 border-slate-800 text-slate-400"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                        isLight ? "bg-slate-100 text-emerald-600" : "bg-slate-800 text-emerald-400"
                      }`}
                    >
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className={`font-bold text-xs ${isLight ? "text-slate-900" : "text-white"}`}>সাবস্ক্রিপশন সচল রয়েছে</h5>
                      <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                        মেয়াদ শেষের <strong>১০ দিন পূর্বে</strong> রিনিউ পেমেন্ট অপশনটি এখানে স্বয়ংক্রিয়ভাবে দৃশ্যমান হবে (আর{" "}
                        {subscription.days_until_renewal_opens || Math.max(0, subscription.days_left - 10)} দিন পর)।
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3.5 py-1.5 rounded-xl border font-mono text-xs font-bold whitespace-nowrap ${
                        isLight
                          ? "bg-slate-50 border-slate-200 text-emerald-700"
                          : "bg-slate-950 border-slate-800 text-emerald-400"
                      }`}
                    >
                      {subscription.days_left} Days Left
                    </span>
                    <button
                      type="button"
                      onClick={fetchSubscriptionData}
                      className={`p-2 rounded-xl border transition-colors cursor-pointer ${
                        isLight
                          ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                          : "bg-slate-800 hover:bg-slate-700 text-slate-300 border-transparent"
                      }`}
                      title="Refresh Status"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 🌟 3. PAYMENT GATEWAYS সেকশন */}
      {activeSection === "gateways" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { id: "bkash", name: "bKash Payment", logoBg: "bg-pink-600" },
              { id: "nagad", name: "Nagad Gateway", logoBg: "bg-orange-600" },
              { id: "sslcommerz", name: "SSLCommerz", logoBg: "bg-blue-600" },
              { id: "custom", name: "Custom Gateway", logoBg: "bg-emerald-600" },
            ].map((gw) => {
              const configured = savedGateways.find((g) => g.gateway_type === gw.id);
              const isSelected = selectedGatewayType === gw.id;
              const isActive = configured?.is_active;

              return (
                <button
                  type="button"
                  key={gw.id}
                  onClick={() => setSelectedGatewayType(gw.id)}
                  className={`p-4 rounded-2xl border text-left transition-all cursor-pointer relative flex flex-col justify-between h-28 ${
                    isSelected
                      ? isLight
                        ? "bg-slate-50 border-emerald-500 ring-2 ring-emerald-500/30 shadow-md"
                        : "bg-slate-800 border-emerald-500 ring-2 ring-emerald-500/30 shadow-lg"
                      : isLight
                      ? "bg-white border-slate-200 hover:border-slate-300 shadow-xs"
                      : "bg-slate-900 border-slate-800 hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`w-8 h-8 rounded-xl ${gw.logoBg} flex items-center justify-center font-black text-white text-xs shadow-xs`}>
                      {gw.name[0]}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                        isActive
                          ? isLight
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : isLight
                          ? "bg-slate-100 text-slate-500"
                          : "bg-slate-950 text-slate-500"
                      }`}
                    >
                      {isActive ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div>
                    <h4 className={`text-xs font-bold mt-2 ${isLight ? "text-slate-900" : "text-white"}`}>{gw.name}</h4>
                    <span className="text-[10px] text-slate-400">
                      {isActive ? "চেকআউটে সক্রিয়" : "কনফিগার করুন"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <form
            onSubmit={handleSaveGateway}
            className={`border rounded-3xl p-6 sm:p-8 space-y-6 text-xs transition-all ${
              isLight ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50" : "bg-slate-900 border-slate-800 shadow-xl"
            }`}
          >
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4 ${
              isLight ? "border-slate-100" : "border-slate-800"
            }`}>
              <div>
                <h3 className={`text-sm font-black flex items-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
                  <WalletCards className="w-4 h-4 text-emerald-500" />
                  <span>Configure {selectedGatewayType.toUpperCase()} Gateway</span>
                </h3>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                  আপনার মার্চেন্ট ক্রেডেনশিয়াল বসিয়ে সক্রিয় করলে কাস্টমার চেকআউটে এই পেমেন্ট অপশনটি দেখতে পাবে।
                </p>
              </div>

              <label
                className={`flex items-center gap-2.5 cursor-pointer px-4 py-2 rounded-xl border transition-colors ${
                  isLight
                    ? "bg-slate-50 border-slate-200 hover:bg-slate-100"
                    : "bg-slate-950 border-slate-800 hover:border-slate-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isGatewayActive}
                  onChange={(e) => setIsGatewayActive(e.target.checked)}
                  className="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                />
                <span className={`font-bold text-xs ${isLight ? "text-slate-900" : "text-white"}`}>
                  {isGatewayActive ? "Gateway Enabled" : "Enable Gateway"}
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>Checkout Display Title *</label>
                <input
                  type="text"
                  required
                  value={gatewayTitle}
                  onChange={(e) => setGatewayTitle(e.target.value)}
                  placeholder="যেমন: bKash Online Payment"
                  className={`w-full px-4 py-2.5 rounded-xl border focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="space-y-1">
                <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>Merchant Sender ID / Username</label>
                <input
                  type="text"
                  value={senderId}
                  onChange={(e) => setSenderId(e.target.value)}
                  placeholder="যেমন: 01700000000 বা username"
                  className={`w-full px-4 py-2.5 rounded-xl border focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="space-y-1">
                <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>API Key / App Key *</label>
                <input
                  type="text"
                  required={isGatewayActive}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="merchant_api_key_xxxxxxxx"
                  className={`w-full px-4 py-2.5 rounded-xl border font-mono focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="space-y-1">
                <label className={`font-bold block ${isLight ? "text-slate-700" : "text-slate-300"}`}>API Secret / App Secret Key *</label>
                <input
                  type="password"
                  required={isGatewayActive}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="••••••••••••••••••••••••"
                  className={`w-full px-4 py-2.5 rounded-xl border font-mono focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label className={`font-bold flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                  <Globe className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Base API Endpoint / Redirect URL (ঐচ্ছিক)</span>
                </label>
                <input
                  type="url"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  placeholder="https://tokenized.pay.bka.sh/v1.2.0-beta"
                  className={`w-full px-4 py-2.5 rounded-xl border font-mono focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="md:col-span-2 pt-2">
                <label
                  className={`flex items-center gap-3 p-3.5 rounded-2xl border cursor-pointer transition-colors ${
                    isLight ? "bg-slate-50 border-slate-200 hover:bg-slate-100" : "bg-slate-950 border-slate-800 hover:border-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSandbox}
                    onChange={(e) => setIsSandbox(e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                  />
                  <div>
                    <span className={`font-bold block ${isLight ? "text-slate-900" : "text-white"}`}>Test / Sandbox Mode</span>
                    <span className="text-[11px] text-slate-400 block mt-0.5">
                      অন করা থাকলে টেস্ট ক্রেডিট বা স্যান্ডবক্স ক্রেডেনশিয়াল কার্যকর হবে। লাইভ পেমেন্টের জন্য টিক তুলে দিন।
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className={`pt-4 border-t flex justify-end ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <button
                type="submit"
                disabled={gatewayLoading}
                className="px-6 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/30 cursor-pointer disabled:opacity-50"
              >
                {gatewayLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Save Gateway Configuration</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 🌟 4. SECURITY & 2FA সেকশন */}
      {activeSection === "security" && (
        <div className="space-y-6">
          <form
            onSubmit={handleSave2FASettings}
            className={`border rounded-3xl p-6 space-y-5 text-xs transition-all ${
              isLight ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50" : "bg-slate-900 border-slate-800"
            }`}
          >
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4 ${
              isLight ? "border-slate-100" : "border-slate-800"
            }`}>
              <div>
                <h3 className={`text-sm font-black flex items-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
                  <Smartphone className="w-4 h-4 text-emerald-500" />
                  <span>Two-Factor Authentication (2FA) via Phone OTP</span>
                </h3>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                  লগইনের সময় ফোনে ৬ ডিজিটের ভেরিফিকেশন ওটিপি কোড পাঠিয়ে অ্যাকাউন্ট সুরক্ষিত রাখুন।
                </p>
              </div>

              <span
                className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border w-fit ${
                  twoFactorEnabled
                    ? isLight
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                    : isLight
                    ? "bg-slate-100 text-slate-500 border-slate-200"
                    : "bg-slate-800 text-slate-400 border-slate-700"
                }`}
              >
                {twoFactorEnabled ? "2FA Active" : "2FA Inactive"}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`font-bold block mb-1.5 flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                  <Phone className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Merchant Verification Phone Number</span>
                </label>
                <input
                  type="tel"
                  placeholder="e.g. 01712345678"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border font-mono focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div className="flex flex-col justify-center">
                <label
                  className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition-colors ${
                    isLight ? "bg-slate-50 border-slate-200 hover:bg-slate-100" : "bg-slate-950 border-slate-800 hover:border-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={twoFactorEnabled}
                    onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-500 cursor-pointer mt-0.5"
                  />
                  <div>
                    <span className={`font-bold block ${isLight ? "text-slate-900" : "text-white"}`}>Enable 2FA Protection</span>
                    <span className="text-[10.5px] text-slate-400 block mt-0.5">
                      অন করা থাকলে লগইনের পর ওটিপি চাইবে।
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className={`pt-3 border-t flex justify-end ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <button
                type="submit"
                disabled={securityLoading}
                className="px-6 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all flex items-center gap-2 cursor-pointer shadow-md"
              >
                {securityLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Save 2FA Settings</span>
              </button>
            </div>
          </form>

          <form
            onSubmit={handleSavePassword}
            className={`border rounded-3xl p-6 space-y-4 text-xs transition-all ${
              isLight ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50" : "bg-slate-900 border-slate-800"
            }`}
          >
            <h3 className={`text-sm font-black flex items-center gap-2 border-b pb-3 ${
              isLight ? "text-slate-900 border-slate-100" : "text-white border-slate-800"
            }`}>
              <KeyRound className="w-4 h-4 text-emerald-500" />
              <span>Change Merchant Password</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div>
                <label className={`font-bold block mb-1 ${isLight ? "text-slate-700" : "text-slate-300"}`}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className={`w-full px-4 py-2.5 rounded-xl border focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>

              <div>
                <label className={`font-bold block mb-1 ${isLight ? "text-slate-700" : "text-slate-300"}`}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className={`w-full px-4 py-2.5 rounded-xl border focus:outline-emerald-500 ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>
            </div>

            <div className={`pt-3 border-t flex justify-end ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <button
                type="submit"
                disabled={passwordLoading}
                className={`px-6 py-2.5 rounded-full font-bold transition-all flex items-center gap-2 cursor-pointer border ${
                  isLight
                    ? "bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200"
                    : "bg-slate-800 hover:bg-slate-700 text-white border-slate-700"
                }`}
              >
                {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Update Password</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 🌟 5. ANNOUNCEMENTS & ALERTS সেকশন */}
      {activeSection === "notifications" && (
        <div className="space-y-6 text-xs">
          <form
            onSubmit={handleSaveTheme}
            className={`border rounded-3xl p-6 space-y-4 transition-all ${
              isLight ? "bg-white border-slate-200/90 shadow-md shadow-slate-200/50" : "bg-slate-900 border-slate-800"
            }`}
          >
            <div className={`flex items-center justify-between border-b pb-3 ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <h3 className={`text-sm font-black flex items-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
                <Megaphone className="w-4 h-4 text-amber-500" />
                <span>Storefront Top Announcement Bar</span>
              </h3>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={themeSettings.show_announcement_bar}
                  onChange={(e) => setThemeSettings({ ...themeSettings, show_announcement_bar: e.target.checked })}
                  className="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                />
                <span className={`font-bold ${isLight ? "text-slate-900" : "text-white"}`}>Live on Website</span>
              </label>
            </div>

            <div>
              <label className={`font-bold block mb-1 ${isLight ? "text-slate-700" : "text-slate-300"}`}>Announcement Text</label>
              <input
                type="text"
                value={themeSettings.announcement_text}
                onChange={(e) => setThemeSettings({ ...themeSettings, announcement_text: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-xl border focus:outline-emerald-500 ${
                  isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-slate-950 border-slate-800 text-white"
                }`}
              />
            </div>

            <div className={`flex justify-end pt-2 border-t ${isLight ? "border-slate-100" : "border-slate-800"}`}>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold flex items-center gap-1.5 shadow-md cursor-pointer"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span>Update Banner</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}